<template>
  <div class="content">
    <div v-if="!exam.id">
      <div class="btn-container u-fill-width u-text-right">
        <back-button></back-button>
      </div>
      <server-error v-bind:error="error.serverError"></server-error>
      <placeholder v-if="!error.serverError" title="Loading exam"></placeholder>
    </div>
    <div v-else>
      <!-- Navigation buttons -->
      <div class="btn-container u-pull-right u-text-right">
        <router-link
          v-if="exam.candidate"
          :to="{ name: 'record-file', params: { id: exam.candidate.id } }"
          class="action-button"
        >
          <button class="btn-dark">
            <i
              class="fa-wrapper fa fa-folder-open normal-icon pad-right"
              aria-hidden="true"
            ></i>
            File
          </button>
        </router-link>
        <back-button></back-button>
      </div>
      <h2 class="font-light">{{ exam.title }}</h2>
      <h4 class="font-light">{{ exam.display_topics }}</h4>
      <exam-info v-if="exam.candidate" v-bind:exam="exam"></exam-info>
      <!-- Error and success message -->
      <server-error v-bind:error="error.serverError"></server-error>
      <space v-if="error.serverError"></space>
      <toast-success
        v-if="success"
        v-bind:label="'Exam Submitted'"
        v-bind:message="successMessage()"
        v-on:close="closeSuccess"
      ></toast-success>
      <!-- Signature fields -->
      <div v-if="showCandidateSignature() || showSupervisorSignature()">
        <div class="divider"></div>
        <h2 class="font-light">Certification</h2>
        <signature-form
          v-if="showCandidateSignature()"
          v-bind:user="exam.candidate"
          v-bind:role="'Candidate'"
          v-bind:error="error.candidate_signature"
          v-on:sign="signExam"
        ></signature-form>
        <div
          v-if="showCandidateSignature() && showSupervisorSignature()"
          class="divider"
        ></div>
        <signature-form
          v-if="showSupervisorSignature()"
          v-bind:user="exam.supervisor"
          v-bind:role="'Supervisor'"
          v-bind:error="error.supervisor_signature"
          v-on:sign="signExam"
        ></signature-form>
        <div class="divider"></div>
      </div>
      <!-- Action buttons -->
      <div class="row">
        <div class="btn-container u-fill-width u-text-right">
          <!-- Submit/Close Exam button -->
          <button
            class="btn-primary u-inline"
            v-on:click="submitExam"
            v-if="showSaveButton()"
            v-bind:disabled="itemsNotMarked"
          >
            <loading-spinner v-show="saving"></loading-spinner>
            <i class="fa-wrapper fa fa-upload" v-show="!saving"></i>
            Submit
          </button>
          <!-- View KDR button -->
          <exam-download-kdr
            v-if="viewKDRSwitch()"
            v-bind:exam="exam"
            label="Create PDF"
          >
          </exam-download-kdr>
          <button class="btn-info u-inline" v-if="viewKDRSwitch()">
            <router-link :to="{ name: 'exam-kdr', params: { id: exam.id } }">
              <i class="fa-wrapper fa fa-file-alt normal-icon pad-right"></i>
              View KDR
            </router-link>
          </button>
        </div>
      </div>
      <!-- The Questions -->
      <div class="divider u-no-margin" v-if="canViewItems()"></div>
      <placeholder
        v-if="!items && !error.serverError && canViewItems()"
        title="Loading questions"
      ></placeholder>
      <div
        v-for="(item, index) in items"
        v-bind:key="item.id"
        class="row no-space"
      >
        <div v-if="index > 0" class="col-12 divider u-no-margin-top"></div>
        <div class="col-1">
          <strong>Q{{ index + 1 }}</strong>
        </div>
        <!-- View exam -->
        <exam-item-view
          v-if="examSwitch() == 'view' || examSwitch() == 'mark'"
          v-bind:item="item"
          v-bind:examId="exam.id"
          v-bind:action="examSwitch()"
        ></exam-item-view>
        <!-- Complete exam -->
        <exam-item-complete
          v-if="examSwitch() == 'complete'"
          v-bind:item="item"
        ></exam-item-complete>
        <exam-item-details
          v-if="item.uuid"
          v-bind:exam="exam"
          v-bind:item="item"
        ></exam-item-details>
      </div>
    </div>
    <!-- Action buttons -->
    <div class="divider u-no-margin-top" v-if="showSaveButton()">
      <space></space>
      <div class="row">
        <div class="btn-container u-fill-width u-text-right">
          <button
            class="btn-primary u-inline"
            v-on:click="submitExam"
            v-if="showSaveButton()"
            v-bind:disabled="itemsNotMarked"
          >
            <loading-spinner v-show="saving"></loading-spinner>
            <i class="fa-wrapper fa fa-upload" v-show="!saving"></i>
            Submit
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<style>
a.uuid-button-mobile {
  padding: 0 !important;
}
a.uuid-button-desktop {
  margin-top: 0.75rem !important;
}
</style>
<script>
import { mapGetters } from "vuex";
import ExamInfo from "@/components/exams/ExamInfo";
import ExamItemView from "@/components/exams/ItemView";
import ExamItemComplete from "@/components/exams/ItemComplete";
import ExamItemDetails from "@/components/exams/ItemDetails";
import ExamDownloadKdr from "@/components/exams/ExamDownloadKDR";
import SignatureForm from "@/components/SignatureForm";
import BackButton from "@/components/BackButton";
import ServerError from "@/components/ServerError";
import Placeholder from "@/components/Placeholder";
import ToastSuccess from "@/components/ToastSuccess";
import { scrollToTop } from "@/functions/scrollToTop";
import LoadingSpinner from "@/components/LoadingSpinner";

export default {
  name: "exam-detail",
  components: {
    ExamInfo,
    ExamItemView,
    ExamItemComplete,
    ExamItemDetails,
    ExamDownloadKdr,
    SignatureForm,
    BackButton,
    ServerError,
    Placeholder,
    ToastSuccess,
    LoadingSpinner
  },
  data: function() {
    return {
      saving: false,
      success: false
    };
  },
  computed: {
    exam: function() {
      var result = this.exams[this.$route.params.id];
      if (!result) {
        return {};
      } else {
        return result;
      }
    },
    items: function() {
      return this.$store.getters["exams/items"](this.$route.params.id);
    },
    itemsNotMarked: function() {
      if (!this.exam.is_closed) {
        return false;
      } else if (this.items) {
        return this.items.filter(i => i.assessment == 0).length > 0;
      } else {
        return false;
      }
    },
    ...mapGetters("exams", ["exams"]),
    ...mapGetters("core", ["error"]),
    ...mapGetters("auth", ["userProfile", "permissions"])
  },
  watch: {
    exam: function() {
      this.getItems();
    }
  },
  methods: {
    examSwitch() {
      // Complete exam:: Only if user is the candidate and exam is open
      if (!this.exam.id) {
        return "hide";
      } else if (
        !this.exam.is_closed &&
        this.exam.candidate.id == this.userProfile.id
      ) {
        return "complete";
      } else if (
        this.exam.is_closed &&
        this.exam.result.status == -1 &&
        this.exam.supervisor.id == this.userProfile.id
      ) {
        return "mark";
      } else {
        // View exam:: If the user owns the exam or is an instructor/examiner
        return "view";
      }
    },
    canViewItems: function() {
      if (!this.exam.candidate) {
        return false;
      }
      // Only get the items if the user has permission to view them **or** if
      // the exam is open and the user is the candidate.
      let is_candidate = this.userProfile.id == this.exam.candidate.id;
      let has_perm = this.permissions.includes("exams.view_item");
      return has_perm || (is_candidate && !this.exam.is_closed);
    },
    getItems: function() {
      // Method to get the items only after the exam exists and only if the
      // User is authorised to view the items.
      if (this.canViewItems()) {
        this.$store.dispatch("exams/getItems", { id: this.$route.params.id });
      } else {
        // Ensure the items for the exam are removed if the person can't view
        // them
        this.$store.commit("exams/removeItems", this.exam.id);
      }
    },
    showSaveButton() {
      if (!this.exam) {
        return false;
      }
      var action = this.examSwitch();
      return (action == "complete" && !this.exam.is_closed) ||
        action == "mark"
    },
    viewKDRSwitch() {
      if (!this.exam.candidate) {
        return false;
      }
      const isCandidate = this.userProfile.id == this.exam.candidate.id;
      const isInstructor = this.userProfile.groups.includes("instructor");
      const isExaminer = this.userProfile.groups.includes("examiner");
      return (
        this.exam.result.status >= 0 &&
        (isCandidate || isInstructor || isExaminer)
      );
    },
    submitExam() {
      scrollToTop();
      this.saving = true;
      this.$store
        .dispatch("exams/closeExam", {
          id: this.$route.params.id,
          items: this.items,
          isSupervisor: this.userProfile.id == this.exam.supervisor.id
        })
        .then(result => {
          // NOTE: The exam is also updated, which will also update the items
          // and/or remove them from view (if appropriate).
          this.saving = false;
          this.success = result;
        });
    },
    successMessage() {
      let message = "The Exam was successfully submitted. ";
      message += "The marks and results have been automatically updated. ";
      if (this.userProfile.id == this.exam.candidate.id) {
        message +=
          "If the exam still shows incomplete this means it needs " +
          "to be reviewed by the supervisor.";
      }
      return message;
    },
    closeSuccess: function() {
      this.success = false;
    },
    signExam(role, code) {
      // Validate form for blank field
      let field = role.toLowerCase() + "_signature";
      this.$store.commit("core/clearErrors");
      // Handle empty fields
      if (code == "") {
        let err = {};
        err[field] = "You must enter your signature code.";
        this.$store.commit("core/setError", err);
        return;
      }
      this.$store.dispatch("exams/signExam", {
        id: this.exam.id,
        username: this.exam[role.toLowerCase()].email,
        signature: code,
        role: role.toLowerCase() + "_signature"
      });
    },
    showCandidateSignature: function() {
      if (!this.exam.id) {
        return false;
      }
      const isInvoled =
        this.exam.candidate.id == this.userProfile.id ||
        this.exam.supervisor.id == this.userProfile.id;
      if (!isInvoled) {
        return false;
      } else {
        return (
          this.exam.result.status >= 0 &&
          this.exam["candidate_signature"] == null
        );
      }
    },
    showSupervisorSignature: function() {
      if (!this.exam.id) {
        return false;
      }
      // Only show if logged in as the supervisor =)
      if (this.exam.supervisor.id != this.userProfile.id) {
        return false;
      } else {
        return (
          this.exam.result.status >= 0 &&
          this.exam["supervisor_signature"] == null
        );
      }
    }
  },
  mounted() {
    // Clear any errors from other exams
    this.$store.commit("core/clearErrors");
    // Get the exam
    this.$store.dispatch("exams/getExam", { id: this.$route.params.id });
    // Note that the "exam" object is watched and the geItems() method is fired
    // whenever the exam object changes.
    scrollToTop();
  }
};
</script>
